import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Menu, X } from 'lucide-react';
import ShieldLogo from './ShieldLogo';

/**
 * Shared marketing-site navigation. Collapses to a hamburger on mobile.
 * Used on Home, About, Contact, Privacy, Terms.
 */
const Nav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (path) => location.pathname === path;
  const navItem = (label) =>
    `text-sm transition-colors ${
      'text-[#1a1a1a]/70 hover:text-[#1a1a1a]'
    }`;
  const activeNavItem = 'text-sm text-[#1a1a1a] font-medium';

  return (
    <nav className="border-b border-[#e0ddd8] sticky top-0 bg-white z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 hover:opacity-70 transition-opacity"
          data-testid="nav-logo-link"
        >
          <ShieldLogo size={24} />
          <span
            className="text-2xl font-semibold"
            style={{ fontFamily: 'Instrument Serif, serif' }}
          >
            Verilock
          </span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/about" className={isActive('/about') ? activeNavItem : navItem('/about')}>
            About
          </Link>
          <Link
            to="/contact"
            className={isActive('/contact') ? activeNavItem : navItem('/contact')}
          >
            Contact
          </Link>
          <Link
            to="/login"
            data-testid="home-nav-login-link"
            className={isActive('/login') ? activeNavItem : navItem('/login')}
          >
            Sign in
          </Link>
          <Button
            onClick={() => navigate('/wizard')}
            data-testid="home-nav-get-started-button"
            variant="outline"
            className="bg-transparent border border-[#0a5c44] text-[#0a5c44] hover:bg-[#0a5c44]/5 hover:text-[#0a5c44] transition-colors h-11"
          >
            Get started
          </Button>
        </div>

        {/* Mobile hamburger */}
        <button
          aria-label="Toggle menu"
          aria-expanded={open}
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((o) => !o)}
          className="md:hidden h-11 w-11 flex items-center justify-center rounded-md border border-[#e0ddd8] hover:bg-[#f5f3ef] transition-colors"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden border-t border-[#e0ddd8] bg-white"
          data-testid="nav-mobile-menu"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1">
            <Link
              to="/about"
              onClick={() => setOpen(false)}
              className={`px-3 py-3 rounded-md text-base ${
                isActive('/about')
                  ? 'bg-[#f5f3ef] text-[#1a1a1a] font-medium'
                  : 'text-[#1a1a1a]/80 hover:bg-[#f5f3ef]'
              }`}
            >
              About
            </Link>
            <Link
              to="/contact"
              onClick={() => setOpen(false)}
              className={`px-3 py-3 rounded-md text-base ${
                isActive('/contact')
                  ? 'bg-[#f5f3ef] text-[#1a1a1a] font-medium'
                  : 'text-[#1a1a1a]/80 hover:bg-[#f5f3ef]'
              }`}
            >
              Contact
            </Link>
            <Link
              to="/login"
              onClick={() => setOpen(false)}
              data-testid="nav-mobile-login-link"
              className={`px-3 py-3 rounded-md text-base ${
                isActive('/login')
                  ? 'bg-[#f5f3ef] text-[#1a1a1a] font-medium'
                  : 'text-[#1a1a1a]/80 hover:bg-[#f5f3ef]'
              }`}
            >
              Sign in
            </Link>
            <Button
              onClick={() => {
                setOpen(false);
                navigate('/wizard');
              }}
              data-testid="nav-mobile-get-started-button"
              variant="outline"
              className="mt-2 bg-transparent border border-[#0a5c44] text-[#0a5c44] hover:bg-[#0a5c44]/5 hover:text-[#0a5c44] transition-colors h-12 text-base"
            >
              Get started
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Nav;
